/**
 * TutorialOverlay — quick onboarding tour for WowCrew + Managers.
 *
 * Scope (user clarification 2026-06-13): WowCrew + Manager flows only.
 * Admin / DM / District Dashboard / Mirror flows are intentionally out
 * of scope — those roles get their own training paths and shouldn't
 * be exposed to crew. The component still mounts for any signed-in
 * user, but `buildSteps` ignores Admin/DM role flags.
 *
 * Design goals (user ask 2026-02 "quick, not everyone wants to spend too much
 * time, ask them if they want to learn, exit anytime, replay from Settings"):
 *   - Skippable from any step (X icon + "Skip tour" link)
 *   - Role-aware — managers see four extra cards (Manager Dashboard,
 *     Eyes on the Biz, Verify Tasks, Reports). Everyone else sees the
 *     shared 14-card crew tour.
 *   - Lightweight: no router, no animations beyond a fade between cards
 *   - "Don't show again" auto-set when they reach the end or hit Skip
 *
 * Mounted from <App> (or HomePage) when the first-login signal fires, and
 * from Settings via the "How to use the app" row to replay on demand.
 */
import { useState, useMemo } from "react";
import {
  X as XIcon, CaretLeft, CaretRight, EnvelopeOpen, UsersThree, ChartBar,
  Sparkle, GearSix, ChatCircleDots,
  Trophy, Bell, Confetti, ClipboardText, ListChecks, QrCode, Percent,
  CalendarBlank, Eye, CheckCircle, ClockClockwise, NotePencil,
} from "@phosphor-icons/react";

// localStorage key — version-tagged so future tutorial reworks can force a
// re-show by bumping the suffix.
//
// 2026-06-12: namespaced by crew_id so that on a shared Zebra each new
// person logging in still sees the welcome the first time. Falls back
// to a global key for the rare case the helper is called without a user
// (e.g. settings "replay tour" before pin entry). Old non-namespaced
// keys are honored on read so existing devices don't re-prompt.
const SEEN_KEY_BASE = "wow_tutorial_seen_v1";

function keyFor(crewId) {
  return crewId ? `${SEEN_KEY_BASE}__${crewId}` : SEEN_KEY_BASE;
}

export function hasSeenTutorial(crewId) {
  try {
    if (localStorage.getItem(keyFor(crewId)) === "1") return true;
    // Back-compat: respect the old global flag too so a device that
    // already cleared the prompt doesn't re-prompt the same user.
    if (crewId && localStorage.getItem(SEEN_KEY_BASE) === "1") return true;
    return false;
  } catch { return false; }
}
export function markTutorialSeen(crewId) {
  try { localStorage.setItem(keyFor(crewId), "1"); } catch { /* ignore */ }
}
export function clearTutorialSeen(crewId) {
  try { localStorage.removeItem(keyFor(crewId)); } catch { /* ignore */ }
}

function buildSteps({ isManager }) {
  // Tutorial scope (2026-06-13 user clarification): WowCrew + Manager
  // only. Admin / DM / District / Mirror flows are intentionally out
  // of scope — those roles have their own real-world training paths
  // and shouldn't be exposed to crew. The function still accepts the
  // role flag but ignores any non-manager-tier flags.
  //
  // Step ordering: all "everyone" cards first so the role mental model
  // stays clean, then the manager-only block at the end. Each step is
  // a tiny chunk — headline, one sentence, a tiny icon.
  const all = [
    // ── EVERYONE ──────────────────────────────────────────────────
    {
      key: "welcome",
      icon: Confetti,
      iconBg: "from-amber-400 to-amber-600",
      title: "Welcome to Wow Crew!",
      body: "60-second tour of the app. Tap the arrows or swipe. You can replay this anytime from Settings.",
    },
    {
      key: "collect",
      icon: EnvelopeOpen,
      iconBg: "from-blue-500 to-blue-700",
      title: "Collect Emails",
      body: "Tap +1 every time you capture an email at the register. Long-press to add multiple. Your count syncs live with the leaderboard.",
    },
    {
      key: "zone_sheet",
      icon: ClipboardText,
      iconBg: "from-amber-500 to-amber-700",
      title: "Zone Sheet",
      body: "Your shift home. See your assigned tasks, today's recovery focus, the crew lineup, and Eyes on the Biz (checkpoint readings + daily KPIs). Open it from the home grid.",
    },
    {
      key: "your_tasks",
      icon: ListChecks,
      iconBg: "from-emerald-500 to-emerald-700",
      title: "Your Tasks Today",
      body: "On the home screen. Tap to acknowledge, mark complete with a photo if needed, or request handoff when you can't finish. Status flows straight to your manager.",
    },
    {
      key: "passcode",
      icon: QrCode,
      iconBg: "from-slate-600 to-slate-800",
      title: "Daily Passcode",
      body: "Scan or open today's receipt/passcode from the home screen so you never have to carry the paper copy.",
    },
    {
      key: "coupon",
      icon: Percent,
      iconBg: "from-rose-500 to-rose-700",
      title: "10% Off Coupon",
      body: "The % pill in the header pops the live coupon barcode whenever a customer needs it.",
    },
    {
      key: "wowcrew",
      icon: UsersThree,
      iconBg: "from-emerald-500 to-emerald-700",
      title: "Wow Crew",
      body: "Roster, profiles, and celebrations. Tap a teammate's photo to open their report card and see today's stats.",
    },
    {
      key: "leaderboards",
      icon: Trophy,
      iconBg: "from-pink-500 to-pink-700",
      title: "Leaderboards",
      body: "Your store, district stores, and district crew rankings — updated live as emails land.",
    },
    {
      key: "inbox",
      icon: ChatCircleDots,
      iconBg: "from-violet-500 to-violet-700",
      title: "Inbox",
      body: "DMs, team blasts, ad-hoc groups, polls, and GIFs. Crew see Inbox as a tile; managers find it in the header.",
    },
    {
      key: "whats_up",
      icon: CalendarBlank,
      iconBg: "from-violet-500 to-violet-700",
      title: "What's Up",
      body: "Events & celebrations calendar — recurring events, birthdays, and work anniversaries all in one place.",
    },
    {
      key: "stories",
      icon: Sparkle,
      iconBg: "from-pink-500 to-pink-700",
      title: "What's Up Stories",
      body: "Quick wins, celebrations, and questions from your store (if your store has Stories enabled). Polls + GIFs supported. Stories auto-expire so the feed stays fresh.",
    },
    {
      key: "contest",
      icon: Trophy,
      iconBg: "from-rose-500 to-rose-700",
      title: "Email Contest",
      body: "Watch your store's weekly capture rate climb the tier ladder. Every Saturday a fresh week opens — keep daily emails high to earn more tickets.",
    },
    {
      key: "bell",
      icon: Bell,
      iconBg: "from-yellow-500 to-yellow-700",
      title: "Bell notifications",
      body: "DMs, mentions, milestones, and missing checkpoint readings (taps open the Zone Sheet → Eyes on the Biz at the exact missing slot). EOD digests + birthdays land here too.",
    },
    // ── MANAGER-ONLY ──────────────────────────────────────────────
    isManager && {
      key: "manager_dashboard",
      icon: ChartBar,
      iconBg: "from-orange-500 to-orange-700",
      title: "Manager Dashboard",
      body: "WTD Financials, today's tasks grouped by crew member, overdue alerts, weekly figures nudges, contests admin, and the EOD recap pipeline. Checkpoints are on the Zone Sheet now — the dashboard banner deep-links you there.",
    },
    isManager && {
      key: "eyes_on_biz",
      icon: Eye,
      iconBg: "from-blue-600 to-blue-800",
      title: "Eyes on the Biz",
      body: "Inside the Zone Sheet. Log 2pm / 5pm / Final checkpoint readings, enter daily ADS + UPT, see Plan vs Actual. All of it flows to the printed Zone Sheet PDF and the EOD recap email.",
    },
    isManager && {
      key: "verify_tasks",
      icon: CheckCircle,
      iconBg: "from-emerald-600 to-emerald-800",
      title: "Verify Tasks",
      body: "Tap a completed task in the Live Feed to verify it, send it back for re-work, or reassign. Overdue tasks bubble up to the bell so nothing slips.",
    },
    isManager && {
      key: "reports",
      icon: ClockClockwise,
      iconBg: "from-rose-600 to-rose-800",
      title: "Reports",
      body: "Activity log + EOD recap. Print a Zone Sheet PDF for any past date and email it from inside the report.",
    },
    // ── EVERYONE ──────────────────────────────────────────────────
    {
      key: "whats_new",
      icon: NotePencil,
      iconBg: "from-indigo-500 to-indigo-700",
      title: "What's New",
      body: "Every release ships with a changelog entry — newest at the top. Reach it from Reports or the What's New link in Settings.",
    },
    {
      key: "settings",
      icon: GearSix,
      iconBg: "from-slate-500 to-slate-700",
      title: "Settings",
      body: "Add your birthday + anniversary so the team can celebrate, set a notification email, and replay this tour any time from 'How to use the app'.",
    },
  ].filter(Boolean);
  return all;
}

export default function TutorialOverlay({ user, onClose }) {
  const isManager = !!user?.is_manager;
  const steps = useMemo(() => buildSteps({ isManager }), [isManager]);
  const [i, setI] = useState(0);
  const total = steps.length;
  const step = steps[i] || steps[0];
  const Icon = step.icon;

  const finish = () => {
    markTutorialSeen();
    onClose && onClose();
  };
  const next = () => {
    if (i >= total - 1) finish();
    else setI((v) => v + 1);
  };
  const prev = () => setI((v) => Math.max(0, v - 1));

  // Touch swipe navigation. Threshold = 40px so taps near the edges of the
  // card don't accidentally trigger a slide. Horizontal only.
  const touch = useState({ x: 0 })[0];
  const onTouchStart = (e) => { touch.x = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = (e.changedTouches?.[0]?.clientX || touch.x) - touch.x;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next();
    else prev();
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 sm:px-6"
      onClick={finish}
      data-testid="tutorial-overlay"
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Top bar: progress + close */}
        <div className="flex items-center gap-2 px-4 pt-4">
          <div className="flex gap-1 flex-1">
            {steps.map((s, idx) => (
              <span
                key={s.key}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  idx <= i ? "bg-blue-600" : "bg-slate-200"
                }`}
                data-testid={`tutorial-progress-${idx}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={finish}
            className="p-1.5 rounded-full hover:bg-slate-100 -mr-1"
            aria-label="Close tutorial"
            data-testid="tutorial-close"
          >
            <XIcon weight="bold" className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Card body */}
        <div className="px-6 py-6 sm:py-8 text-center flex-1 overflow-y-auto" data-testid={`tutorial-step-${step.key}`}>
          <div
            className={`mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br ${step.iconBg} flex items-center justify-center shadow-md`}
          >
            <Icon weight="bold" className="w-9 h-9 text-white" />
          </div>
          <h2
            className="text-xl sm:text-2xl font-black tracking-tight mb-2"
            style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "#1A1A1A" }}
          >
            {step.title}
          </h2>
          <p className="text-sm text-slate-600 leading-relaxed max-w-sm mx-auto">
            {step.body}
          </p>
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-3 font-bold">
            Step {i + 1} of {total}
          </p>
        </div>

        {/* Footer controls */}
        <div className="flex items-center justify-between gap-2 px-4 pb-5 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={prev}
            disabled={i === 0}
            className="text-xs font-bold text-slate-500 hover:text-slate-700 disabled:opacity-30 disabled:pointer-events-none px-3 py-2 inline-flex items-center gap-1"
            data-testid="tutorial-prev"
          >
            <CaretLeft weight="bold" className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            onClick={finish}
            className="text-xs font-bold text-slate-400 hover:text-slate-600 underline"
            data-testid="tutorial-skip"
          >
            Skip tour
          </button>
          <button
            type="button"
            onClick={next}
            className="fb-btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1"
            data-testid="tutorial-next"
          >
            {i >= total - 1 ? "Got it!" : "Next"}
            {i < total - 1 && <CaretRight weight="bold" className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * WelcomePrompt — first-login splash that asks "Want a quick tour?"
 * Shows ONCE per crew (namespaced by user.crew_id). Tapping Yes mounts
 * the TutorialOverlay; tapping Skip marks-seen without opening it.
 */
export function WelcomePrompt({ user, onStart, onSkip }) {
  const crewId = user?.crew_id;
  return (
    <div
      className="fixed inset-0 z-[200] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="tutorial-welcome-prompt"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 mx-auto mb-3 flex items-center justify-center shadow-md">
          <Confetti weight="bold" className="w-9 h-9 text-white" />
        </div>
        <h2
          className="text-xl font-black tracking-tight mb-1"
          style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "#1A1A1A" }}
        >
          Welcome to Wow Crew!
        </h2>
        <p className="text-sm text-slate-600 mb-5">
          Want a quick 60-second tour? We'll walk through the main pages so you know what does what.
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => { markTutorialSeen(crewId); onStart && onStart(); }}
            className="fb-btn-primary rounded-full py-2.5 text-sm font-bold"
            data-testid="tutorial-welcome-start"
          >
            Show me around
          </button>
          <button
            type="button"
            onClick={() => { markTutorialSeen(crewId); onSkip && onSkip(); }}
            className="text-xs font-bold text-slate-500 hover:text-slate-700 py-2"
            data-testid="tutorial-welcome-skip"
          >
            Skip — I'll figure it out
          </button>
        </div>
      </div>
    </div>
  );
}
